import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { LoadingButton } from "@src/appV2/lib/LoadingButton/LoadingButton";
import { Illustration, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { v5 as uuidv5 } from "uuid";

interface QuizStepProps {
  quiz: Array<{
    options: Array<{ text: string; isCorrect: boolean }>;
    question: string;
  }>;
  isOrientationBeingSubmitted: boolean;
  isOrientationDataLoading: boolean;
  onContinue: () => void;
  stepInfo: string;
  // Useful for tracking events in the recap step
  workerId?: string;
  assessmentId?: string;
  stepNumber?: number;
}

type QuizFormData = Record<string, string>;

// This is the namespace for generating UUIDs for the quiz questions and options
// It can be any UUID.
const namespace = "3e8376b7-24f7-4504-9703-9d015066c974";

export function QuizStep(props: QuizStepProps) {
  const {
    quiz,
    stepInfo,
    isOrientationBeingSubmitted,
    isOrientationDataLoading,
    onContinue,
    workerId,
    assessmentId,
    stepNumber,
  } = props;

  const { handleSubmit, control } = useForm<QuizFormData>();
  const { showInfoToast } = useToast();
  const [feedbackState, setFeedbackState] = useState<"correct" | "incorrect" | undefined>(
    undefined
  );

  const commonLogProperties = useMemo(() => {
    return {
      ...(workerId && { workerId }),
      ...(assessmentId && { assessmentId }),
      ...(stepNumber && { stepNumber }),
    };
  }, [workerId, assessmentId, stepNumber]);

  const quizWithIds = useMemo(() => {
    return quiz.map((question) => ({
      ...question,
      id: uuidv5(question.question, namespace),
      options: question.options.map((option) => ({
        ...option,
        id: uuidv5(option.text, namespace),
      })),
    }));
  }, [quiz]);

  useLogEffect(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_QUIZ_STEP_VIEWED, commonLogProperties);

  // Watch all form values to determine if all questions are answered
  const watchedValues = useWatch({ control });

  const isAllQuestionsAnswered = useMemo(() => {
    return quizWithIds.every((question) => watchedValues?.[question.id]);
  }, [quizWithIds, watchedValues]);

  // Reset feedback state when answers change
  useEffect(() => {
    setFeedbackState(undefined);
  }, [watchedValues]);

  function onSubmit(data: QuizFormData) {
    const results = quizWithIds.map((question) => {
      const selectedOptionId = data[question.id];
      const selectedOption = question.options.find((opt) => opt.id === selectedOptionId);
      const correctOption = question.options.find((opt) => opt.isCorrect);

      return {
        question: question.question,
        selectedAnswer: selectedOption?.text ?? "",
        correctAnswer: correctOption?.text ?? "",
        isCorrect: selectedOption?.isCorrect ?? false,
      };
    });

    const isCorrect = results.every((result) => result.isCorrect);

    // Log segment event with quiz results
    logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_QUIZ_STEP_COMPLETED, {
      ...commonLogProperties,
      quizResults: results,
      isCorrect,
    });

    if (isCorrect) {
      setFeedbackState("correct");

      // Auto-proceed after 1 second
      setTimeout(() => {
        setFeedbackState(undefined);
        onContinue();
      }, 1000);
    } else {
      setFeedbackState("incorrect");
    }
  }

  async function handleContinueClick() {
    if (feedbackState === "correct") {
      // We don't want user to perform action while "correct" state feedback is being shown
      // because it will be cleared and user will automatically continue after 1 second
      return;
    }

    if (!isAllQuestionsAnswered) {
      showInfoToast("Please choose an answer before continuing");
      return;
    }

    await handleSubmit(onSubmit)();
  }

  const shouldDisableButton =
    !isAllQuestionsAnswered || isOrientationDataLoading || feedbackState === "incorrect";

  return (
    <Stack flex={1} pt={6} justifyContent="space-between">
      <Stack spacing={4}>
        <Title bold variant="h3" component="h3">
          Which is the correct answer?
        </Title>
        <Stack spacing={4}>
          <Stack direction="row" alignItems="center" gap={4}>
            <Box>
              <Illustration type="calendar-alert-small" />
            </Box>
            <Divider sx={{ flexGrow: 1 }} />
          </Stack>

          <Stack spacing={6} px={4}>
            {quizWithIds.map((item) => (
              <FormControl key={item.id}>
                <Stack spacing={4}>
                  <Text bold variant="body1" color="text.secondary">
                    {item.question}
                  </Text>

                  <Controller
                    name={item.id}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value}
                        onChange={(_, newValue) => {
                          field.onChange(newValue);
                        }}
                      >
                        <Stack spacing={4}>
                          {item.options.map((option) => (
                            <FormControlLabel
                              key={option.id}
                              sx={(theme) => ({
                                color: theme.palette.text.primary,
                                fontWeight: "bold",
                              })}
                              value={option.id}
                              control={<Radio />}
                              label={option.text}
                              disabled={feedbackState === "correct"}
                            />
                          ))}
                        </Stack>
                      </RadioGroup>
                    )}
                  />
                </Stack>
              </FormControl>
            ))}
          </Stack>
        </Stack>
      </Stack>

      <Stack justifyContent="space-between" alignItems="center" spacing={4}>
        {feedbackState === "correct" && (
          <Text
            variant="body1"
            sx={(theme) => ({ color: theme.palette.success.main, fontWeight: "bolder" })}
          >
            Correct!
          </Text>
        )}
        {feedbackState === "incorrect" && (
          <Text
            variant="body1"
            sx={(theme) => ({ color: theme.palette.error.main, fontWeight: "bolder" })}
          >
            Incorrect answer
          </Text>
        )}
        <Text bold variant="h6" color="text.secondary">
          {stepInfo}
        </Text>

        <Box
          sx={{
            width: "100%",
          }}
          // Disabled button can't be clicked but we want to give feedback to the user if they try to click this area
          onClick={() => {
            if (!isAllQuestionsAnswered) {
              showInfoToast("Please choose an answer before continuing");
            }
          }}
        >
          <LoadingButton
            fullWidth
            variant="contained"
            LinkComponent="button"
            disabled={shouldDisableButton}
            isLoading={isOrientationBeingSubmitted}
            sx={{ alignSelf: "center" }}
            onClick={async () => {
              await handleContinueClick();
            }}
          >
            Continue
          </LoadingButton>
        </Box>
      </Stack>
    </Stack>
  );
}
